import {
  Color,
  FontWeight,
  Markdown,
  TableVariant,
  Typography,
  TypographyElement,
  TypographyVariant,
  TableCellData as UITableCellData,
} from '@elearning-platform/ui';
import React, { useContext, useEffect, useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { PageDataContext } from '../../components/page';
import { TableCellType, TableType } from '../../enums';
import { PageData, TableCellData } from '../../interfaces';

import {
  Container,
  DescriptionContainer,
  HeaderContainer,
  StyledDescription,
  StyledTable,
  StyledTypography,
  TableWrapper,
} from './tableWithText.styled';
import { TableWithTextProps } from './tableWithText.types';

export const TableWithText: React.FC<TableWithTextProps> = (props: TableWithTextProps): JSX.Element => {
  const { callComplete, content, completed }: TableWithTextProps = props;
  const { t }: TransProps<never> = useTranslation();
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const { title, description, slidesOverallCounter, currentSlideCounter, baseUrl }: Partial<PageData> = dataCtx ?? {};

  useEffect((): void => {
    if (!completed) {
      callComplete();
    }
  }, [completed, callComplete]);

  const tableVariant: TableVariant = useMemo((): TableVariant => {
    switch (content.tableVariant) {
      case TableType.Accented:
        return TableVariant.Accented;
      case TableType.WithImages:
        return TableVariant.WithImages;
      case TableType.EvenColumns:
        return TableVariant.EvenColumns;
      case TableType.InsuranceAmount:
        return TableVariant.InsuranceAmount;
      case TableType.Default:
      default:
        return TableVariant.Default;
    }
  }, [content.tableVariant]);

  return (
    <Container>
      <HeaderContainer>
        <Typography color={Color.Gray600} fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextSM}>
          <Markdown
            text={t('competence.app.scrollLesson.componentsV2.tableWithText.slides', { currentSlideCounter, slidesOverallCounter })}
          />
        </Typography>
        <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayXS}>
          {title}
        </Typography>
      </HeaderContainer>
      {!!description && (
        <DescriptionContainer>
          <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
            <StyledDescription text={description} />
          </Typography>
        </DescriptionContainer>
      )}
      <TableWrapper>
        <StyledTable
          content={content.tableContent.map((array: TableCellData[]): UITableCellData[] => array
            .map((item: TableCellData): UITableCellData => ({ ...item, content: item.type === TableCellType.WithImage
              ? `${baseUrl}${item.content}`
              : item.content,
            }))
          )}
          variant={tableVariant}
        />
      </TableWrapper>
      {!!content.disclaimer && (
        <StyledTypography variant={TypographyVariant.TextLG}>
          <Markdown text={content.disclaimer} />
        </StyledTypography>
      )}
    </Container>
  );
};
